import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { useCallback, useMemo, useState } from "react";
import { Platform, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { practiceApi } from "@/api";
import { publicConfig } from "@/config/public";
import { Spacing, type ThemeColors } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";

import { resolvePracticePackCheckout } from "./pack-checkout-result";
import type { PackCheckoutProps } from "./pack-checkout.types";
import { getStripe } from "./stripe-js";

const FONT_FAMILY = Platform.select({
  ios: "Avenir Next",
  android: "sans-serif",
  default: undefined,
});

/**
 * In-app practice pack checkout for web.
 *
 * Packs used to redirect the browser to `checkout.stripe.com` — leaving the
 * app mid-purchase for a Stripe-branded page while the subscription flow
 * beside it stayed in-app. This mounts the same Checkout Session inside our
 * own screen instead.
 *
 * ── Why Checkout and not Elements ────────────────────────────────────────────
 *
 * The subscription flow uses `PaymentElement`, which would let us style the
 * form from our own theme tokens. Packs deliberately do not, because a pack's
 * money paths are keyed on the Checkout SESSION: `session.id` is the payment
 * reference a pack is credited under, and `revokeRefundedCharge` recognises a
 * refunded pack by finding a session from the charge's payment_intent. A bare
 * PaymentIntent would have no session, and that refund path would fall through
 * to its "otherwise this is a subscription charge" branch — cancelling the
 * buyer's Plus over a 2.50 refund. Embedded Checkout keeps the session, so
 * crediting, deduping and refunds are untouched; only the surface moved.
 *
 * The visible cost of that trade: Embedded Checkout has no client-side
 * `appearance` option (see `EmbeddedCheckoutProvider`, which accepts only
 * clientSecret/fetchClientSecret and the change handlers). Its palette comes
 * from the Stripe Dashboard's branding settings, so matching the dark app is
 * done there rather than here.
 */
export function PackCheckout({ packId }: PackCheckoutProps) {
  useTranslation();
  const c = useTheme();
  const styles = useMemo(() => createStyles(c), [c]);
  const [failed, setFailed] = useState(false);

  /**
   * Stripe calls this once when the form mounts; a session must exist before
   * there is anything to render, so unlike the subscription's deferred
   * Elements there is no way to avoid creating one for someone who opens the
   * page and leaves. Unpaid sessions expire on Stripe's side.
   *
   * The pack id goes to the server as an id only — the price, the currency and
   * the customer are all resolved there, exactly as they were for the hosted
   * page.
   */
  const fetchClientSecret = useCallback(async () => {
    try {
      const start = resolvePracticePackCheckout(
        await practiceApi.purchase(packId),
      );
      if (start.kind === "embedded") return start.clientSecret;
      if (start.kind === "hosted") {
        window.location.assign(start.url);
        return new Promise<string>(() => {});
      }
      setFailed(true);
      throw new Error("practice_pack_session_invalid");
    } catch {
      // Surfacing our own message beats Stripe.js throwing inside its iframe
      // over a rejected promise, which shows the buyer nothing at all.
      setFailed(true);
      throw new Error("practice_pack_session_failed");
    }
  }, [packId]);

  const options = useMemo(() => ({ fetchClientSecret }), [fetchClientSecret]);

  if (!publicConfig.stripe.publishableKey || failed || !packId) {
    return <Text style={styles.note}>{i18n.t("practice.errors.packs")}</Text>;
  }

  return (
    <View style={styles.frame}>
      <EmbeddedCheckoutProvider
        key={packId}
        stripe={getStripe()}
        options={options}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </View>
  );
}

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    // Checkout sizes its own iframe; this only bounds and centres it so it
    // does not run the full width of a desktop window.
    frame: {
      width: "100%",
      maxWidth: 680,
      alignSelf: "center",
      paddingTop: Spacing.md,
    },
    note: {
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      lineHeight: 21,
      textAlign: "center",
      paddingVertical: Spacing.xl,
      color: c.textSecondary,
    },
  });
}
